import { Component, ChangeDetectionStrategy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { State, RequestState } from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type {
  AvailablePlanResponse,
  GetAvailablePlansApiResponse,
  PlanFeaturesMapResponse,
  PlanLimitMetricResponse,
} from '@core/api/workspace/models';
import { PlanFeatureFlagResponseDisplay } from '@core/api/workspace/models';

const FEATURE_ORDER: ReadonlyArray<keyof PlanFeaturesMapResponse> = [
  'manualFormBuilder',
  'aiFormBuilder',
  'basicElements',
  'advancedElements',
  'multiPageForms',
  'fileUploads',
  'formTemplates',
  'dataLibrary',
  'contactsAndGroups',
  'integrations',
  'removeBranding',
  'customDomain',
  'prioritySupport',
];

export type PlanLimitRow = {
  label: string;
  metric: PlanLimitMetricResponse;
};

export type FeatureRow = {
  key: keyof PlanFeaturesMapResponse;
  label: string;
};

export type FeatureCell =
  | { kind: 'check' }
  | { kind: 'dash' }
  | { kind: 'text'; text: string };

@Component({
  selector: 'app-pricing-page',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './pricing-page.html',
  styleUrl: './pricing-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);

  readonly plansState = new State<GetAvailablePlansApiResponse>();

  readonly RequestState = RequestState;

  readonly billingPeriod = signal<'monthly' | 'yearly'>('monthly');

  readonly customPricingLabel = 'Custom pricing';

  ngOnInit(): void {
    this.fetchPlans();
  }

  private fetchPlans(): void {
    this.executeRequest({
      state: this.plansState,
      request: this.userAPIService.getAvailablePlans(),
      onSuccess: (response: GetAvailablePlansApiResponse) => {
        this.plansState.notifySuccess(response);
      },
      onFailed: (error) => {
        console.error('Failed to fetch plans:', error);
      },
      handleError: true,
      errorMessage: 'Failed to load pricing plans',
    });
  }

  getPlans(): AvailablePlanResponse[] {
    return this.plansState.response()?.data ?? [];
  }

  comparisonPlans(): AvailablePlanResponse[] {
    return [...this.getPlans()].sort((a, b) => a.displayOrder - b.displayOrder);
  }

  featureRows(): FeatureRow[] {
    const plans = this.comparisonPlans();
    const sample = plans[0];
    return FEATURE_ORDER.map((key) => ({
      key,
      label: sample?.features[key]?.label?.trim() || key,
    }));
  }

  featureCell(
    plan: AvailablePlanResponse,
    key: keyof PlanFeaturesMapResponse,
  ): FeatureCell {
    const display = plan.features[key]?.display;
    if (display === PlanFeatureFlagResponseDisplay.included) {
      return { kind: 'check' };
    }
    if (!display || display === PlanFeatureFlagResponseDisplay.none) {
      return { kind: 'dash' };
    }
    return {
      kind: 'text',
      text: display.charAt(0).toUpperCase() + display.slice(1),
    };
  }

  toggleBillingPeriod(period: 'monthly' | 'yearly'): void {
    this.billingPeriod.set(period);
  }

  getPrice(plan: AvailablePlanResponse): number | null {
    if (!plan.price) {
      return null;
    }
    return this.billingPeriod() === 'monthly'
      ? plan.price.monthlyPrice
      : plan.price.yearlyPrice;
  }

  hasPrice(plan: AvailablePlanResponse): boolean {
    return plan.price != null;
  }

  getBillingText(): string {
    return this.billingPeriod() === 'monthly' ? '/month' : '/year';
  }

  getSavingsPercentage(plan: AvailablePlanResponse): number {
    if (!plan.price) {
      return 0;
    }
    const monthlyTotal = plan.price.monthlyPrice * 12;
    const yearlyPrice = plan.price.yearlyPrice;
    if (monthlyTotal === 0 || yearlyPrice === 0) return 0;
    return Math.round(((monthlyTotal - yearlyPrice) / monthlyTotal) * 100);
  }

  getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = {
      USD: '$',
      INR: '₹',
    };
    return symbols[currency] || '$';
  }

  formatPrice(price: number): string {
    return price.toLocaleString();
  }

  limitRows(plan: AvailablePlanResponse): PlanLimitRow[] {
    const { limits } = plan;
    const rows: PlanLimitRow[] = [
      { label: 'Forms', metric: limits.formCounts },
      { label: 'Monthly submissions', metric: limits.submissionsPerMonth },
      { label: 'Team members', metric: limits.membersCount },
      { label: 'Storage', metric: limits.storageBytes },
      { label: 'Contacts', metric: limits.contactsCount },
      { label: 'Data libraries', metric: limits.dataLibraryCount },
    ];

    if (limits.aiCreditsPerMonth.isAvailable) {
      rows.push({ label: 'AI credits', metric: limits.aiCreditsPerMonth });
    }
    if (limits.workflowActionsPerMonth.isAvailable) {
      rows.push({
        label: 'Workflow actions',
        metric: limits.workflowActionsPerMonth,
      });
    }

    return rows;
  }

  getSignupQueryParams(plan: AvailablePlanResponse): {
    planKey: string;
    billingCycle: string;
  } {
    return {
      planKey: plan.key,
      billingCycle: this.billingPeriod() === 'monthly' ? 'MONTHLY' : 'YEARLY',
    };
  }

  getPlanCtaLink(plan: AvailablePlanResponse): string {
    return plan.canSignup ? '/signup' : '/contact-sales';
  }

  getPlanCtaQueryParams(
    plan: AvailablePlanResponse,
  ): Record<string, string> | null {
    if (!plan.canSignup) {
      return { planKey: plan.key };
    }
    return this.getSignupQueryParams(plan);
  }
}
